import {
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  format,
  isSameDay,
  isSameYear,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
} from 'date-fns';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown } from 'shared/assets/icons';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { weekdaysNumberMap } from 'shared/constants';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { DateRange } from 'shared/types/date-range';
import { WeekDays } from 'shared/types/week-days';

import * as Styled from './date-range-select.style';

enum RangeOptions {
  Custom = 'custom',
  Week = 'week',
  Month = 'month',
  Quarter = 'quarter',
  Year = 'year',
}

export type DateRangeSelectProps = {
  value: DateRange;
  onChange: (value: DateRange) => void;
  weekStartsOn: WeekDays;
  customRange?: DateRange;
};

export const DateRangeSelect: React.FC<DateRangeSelectProps> = ({
  value,
  customRange,
  onChange,
  weekStartsOn,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const _value = useMemo(() => {
    if (
      customRange &&
      isSameDay(value.startDate, customRange?.startDate) &&
      isSameDay(value.endDate, customRange?.endDate)
    ) {
      return RangeOptions.Custom;
    }
    const weekOptions = { weekStartsOn: weekdaysNumberMap[weekStartsOn] };
    if (
      isSameDay(value.startDate, startOfWeek(new Date(), weekOptions)) &&
      isSameDay(value.endDate, endOfWeek(new Date(), weekOptions))
    ) {
      return RangeOptions.Week;
    }
    if (
      isSameDay(value.startDate, startOfMonth(new Date())) &&
      isSameDay(value.endDate, endOfMonth(new Date()))
    ) {
      return RangeOptions.Month;
    }
    if (
      isSameDay(value.startDate, startOfQuarter(new Date())) &&
      isSameDay(value.endDate, endOfQuarter(new Date()))
    ) {
      return RangeOptions.Quarter;
    }
    if (
      isSameDay(value.startDate, startOfYear(new Date())) &&
      isSameDay(value.endDate, endOfYear(new Date()))
    ) {
      return RangeOptions.Year;
    }
  }, [customRange, value.endDate, value.startDate, weekStartsOn]);

  const onCustom = () => {
    onChange(customRange!);
    closeMenu();
  };

  const onWeek = () => {
    const weekOptions = { weekStartsOn: weekdaysNumberMap[weekStartsOn] };
    onChange({
      startDate: startOfWeek(new Date(), weekOptions),
      endDate: endOfWeek(new Date(), weekOptions),
    });
    closeMenu();
  };

  const onMonth = () => {
    onChange({
      startDate: startOfMonth(new Date()),
      endDate: endOfMonth(new Date()),
    });
    closeMenu();
  };

  const onQuarter = () => {
    onChange({
      startDate: startOfQuarter(new Date()),
      endDate: endOfQuarter(new Date()),
    });
    closeMenu();
  };

  const onYear = () => {
    onChange({
      startDate: startOfYear(new Date()),
      endDate: endOfYear(new Date()),
    });
    closeMenu();
  };

  const customRangeLabel = useMemo(
    () =>
      customRange?.startDate && customRange?.endDate
        ? isSameYear(customRange.startDate, customRange.endDate)
          ? `${format(customRange.startDate, 'MMM d')} - ${format(customRange.endDate, 'MMM d')}`
          : `${format(customRange.startDate, 'MMM d, yy')} - ${format(customRange.endDate, 'MMM d, yy')}`
        : undefined,
    [customRange],
  );

  const labelsMap = useMemo(
    () => ({
      [RangeOptions.Custom]: customRangeLabel,
      [RangeOptions.Week]: t(
        'general.labels.goal-progress.date-range.this-week.label',
      ),
      [RangeOptions.Month]: t(
        'general.labels.goal-progress.date-range.this-month.label',
      ),
      [RangeOptions.Quarter]: t(
        'general.labels.goal-progress.date-range.this-quarter.label',
      ),
      [RangeOptions.Year]: t(
        'general.labels.goal-progress.date-range.this-year.label',
      ),
    }),
    [customRangeLabel, t],
  );

  return (
    <Styled.Container>
      <Button
        ref={buttonRef}
        variant={Variants.Text}
        endIcon={ChevronDown}
        onClick={openMenu}
        size={Sizes.Small}
        color={Colors.Inherit}
      >
        {labelsMap[_value ?? RangeOptions.Custom]}
      </Button>

      {menuOpen && (
        <PopupMenu
          referenceElement={buttonRef}
          onOutside={closeMenu}
          position="bottom-end"
        >
          <PopupMenuList>
            {!!customRange && (
              <PopupMenuListItem>
                <PopupMenuButton
                  onClick={onCustom}
                  start={
                    _value === RangeOptions.Custom ? (
                      <Icon icon={Check} />
                    ) : undefined
                  }
                  indentationLevel={_value !== RangeOptions.Custom ? 1 : 0}
                >
                  {customRangeLabel}
                </PopupMenuButton>
              </PopupMenuListItem>
            )}
            <PopupMenuListItem>
              <PopupMenuButton
                onClick={onWeek}
                start={
                  _value === RangeOptions.Week ? (
                    <Icon icon={Check} />
                  ) : undefined
                }
                indentationLevel={_value !== RangeOptions.Week ? 1 : 0}
              >
                {labelsMap[RangeOptions.Week]}
              </PopupMenuButton>
            </PopupMenuListItem>
            <PopupMenuListItem>
              <PopupMenuButton
                onClick={onMonth}
                start={
                  _value === RangeOptions.Month ? (
                    <Icon icon={Check} />
                  ) : undefined
                }
                indentationLevel={_value !== RangeOptions.Month ? 1 : 0}
              >
                {labelsMap[RangeOptions.Month]}
              </PopupMenuButton>
            </PopupMenuListItem>
            <PopupMenuListItem>
              <PopupMenuButton
                onClick={onQuarter}
                start={
                  _value === RangeOptions.Quarter ? (
                    <Icon icon={Check} />
                  ) : undefined
                }
                indentationLevel={_value !== RangeOptions.Quarter ? 1 : 0}
              >
                {labelsMap[RangeOptions.Quarter]}
              </PopupMenuButton>
            </PopupMenuListItem>
            <PopupMenuListItem>
              <PopupMenuButton
                onClick={onYear}
                start={
                  _value === RangeOptions.Year ? (
                    <Icon icon={Check} />
                  ) : undefined
                }
                indentationLevel={_value !== RangeOptions.Year ? 1 : 0}
              >
                {labelsMap[RangeOptions.Year]}
              </PopupMenuButton>
            </PopupMenuListItem>
          </PopupMenuList>
        </PopupMenu>
      )}
    </Styled.Container>
  );
};
